"use client"
import { zodResolver } from "@hookform/resolvers/zod"
import type { FragmentOf, ResultOf } from "@opensea/graphql"
import { graphql, readFragment, useQuery } from "@opensea/graphql"
import { useToasts } from "@opensea/ui-kit"
import { useLocalStorage } from "@opensea/ui-kit/hooks"
import { useCallback, useState } from "react"
import { FormProvider, useForm, useWatch } from "react-hook-form"
import { NETWORK_ONLY_OPERATION_CONTEXT } from "../../../constants"
import { useSyncFormFields } from "../../../hooks/useSyncFormField"
import { collectionOfferFlowSchema } from "../../../lib/zod/schema"
import { FlowActions, FlowState } from "../../../stores/flowStore"
import type { Price, Trait } from "../../../types"
import type { CreateCollectionOfferFlowState } from "../../flow-state"
import { useEndFlowOnAccountChange } from "../../hooks/useEndFlowOnAccountChange"
import {
  useItemAttribute,
  useItemAttributes,
} from "../../hooks/useItemAttribute"
import {
  useLiveUpdateCollectionsBestOrders,
  useLiveUpdateCollectionsBestOrdersFragment,
} from "../../hooks/useLiveUpdateCollectionsBestOrders"
import { CreateCollectionOfferToast } from "../../ToastHandler/CreateCollectionOfferToast/CreateCollectionOfferToast"
import {
  DEFAULT_EXPIRATION,
  OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE,
  OFFER_EXPIRATION_LOCAL_STORAGE,
} from "../constants"
import { CollectionOfferPriceWarningModal } from "./components/CollectionOfferPriceWarningModal"
import { CreateCollectionOfferFlowReview } from "./components/CreateCollectionOfferFlowReview"
import { CreateCollectionOfferFlowReview_collectionFragment } from "./components/CreateCollectionOfferFlowReview/CreateCollectionOfferFlowReview.fragment"
import {
  CreateCollectionOfferForm,
  CreateCollectionOfferFormSkeleton,
} from "./components/CreateCollectionOfferForm"
import { CreateCollectionOfferFormFragment } from "./components/CreateCollectionOfferForm/CreateCollectionOfferForm.fragment"
import type { CreateCollectionOfferFormValues } from "./types"

const CreateCollectionOfferFlowBaseFragment = graphql(
  `
    fragment CreateCollectionOfferFlowBase on Collection {
      id
      slug
      imageUrl
      fees {
        totalCreatorFee {
          isRequired
          feeBasisPoints
        }
        openseaFee {
          feeBasisPoints
        }
      }
      floorPrice {
        id
        pricePerItem {
          token {
            unit
          }
        }
      }
      topOffer {
        id
        pricePerItem {
          token {
            unit
          }
        }
      }
      stats {
        totalSupply
      }
      chain {
        identifier
        wrappedNativeCurrency {
          address
        }
        offerCurrency {
          symbol
          address
        }
        nativeCurrency {
          symbol
        }
      }

      ...CreateCollectionOfferForm
      ...CreateCollectionOfferReview_collection
      ...useLiveUpdateCollectionsBestOrders
    }
  `,
  [
    CreateCollectionOfferFormFragment,
    CreateCollectionOfferFlowReview_collectionFragment,
    useLiveUpdateCollectionsBestOrdersFragment,
  ],
)

const CreateCollectionOfferQuery = graphql(
  `
    query CreateCollectionOfferFlowQuery(
      $slug: String!
      $attributesLimit: Int!
    ) {
      collectionBySlug(slug: $slug) {
        __typename
        ... on Collection {
          id
          name
          ...CreateCollectionOfferFlowBase
        }
      }
    }
  `,
  [CreateCollectionOfferFlowBaseFragment],
)

type CreateCollectionOfferFlowProps = Pick<
  CreateCollectionOfferFlowState,
  "slug" | "trait" | "traits" | "initialPrice"
>

export function CreateCollectionOfferFlow({
  slug,
  trait,
  traits,
  initialPrice,
}: CreateCollectionOfferFlowProps) {
  const [{ data, fetching }] = useQuery({
    query: CreateCollectionOfferQuery,
    variables: {
      slug,
      attributesLimit: 50,
    },
    context: NETWORK_ONLY_OPERATION_CONTEXT,
  })

  useEndFlowOnAccountChange()

  if (fetching) {
    return <CreateCollectionOfferFormSkeleton />
  }

  const collection =
    data?.collectionBySlug?.__typename === "Collection"
      ? data.collectionBySlug
      : null

  if (!collection) {
    throw new Error("Collection not found")
  }

  return (
    <CreateCollectionOfferFlowBase
      collection={collection}
      initialPrice={initialPrice}
      trait={trait}
      traits={traits}
    />
  )
}

function CreateCollectionOfferFlowBase({
  collection: collectionKey,
  trait: initialTrait,
  traits: initialTraits,
  initialPrice,
}: {
  collection: FragmentOf<typeof CreateCollectionOfferFlowBaseFragment>
  trait?: Trait
  traits?: Trait[]
  initialPrice?: Price
}) {
  const { addCustomToast } = useToasts()

  const [collection] = useLiveUpdateCollectionsBestOrders([
    readFragment(CreateCollectionOfferFlowBaseFragment, collectionKey),
  ])
  const offerSymbol = collection.chain.offerCurrency.symbol

  const methods = useForm({
    defaultValues: useDefaultFormValues(
      collection,
      initialTrait,
      initialTraits,
      initialPrice,
    ),
    mode: "onChange",
    resolver: zodResolver(collectionOfferFlowSchema),
  })

  const offers = useWatch({ control: methods.control, name: "offers" })
  const trait = offers[0]?.trait
  const traits = offers[0]?.traits || []
  const isMultiTraitMode =
    initialTraits !== undefined && initialTraits.length > 1

  const { attribute } = useItemAttribute({
    collectionId: collection.id,
    traitType: trait?.traitType || "",
    value: trait?.value || "",
    pause: isMultiTraitMode || !trait,
  })

  const { attributes } = useItemAttributes({
    traits,
    collectionId: collection.id,
    pause: !isMultiTraitMode || traits.length === 0,
  })

  const getAggregateTraitFloorPrice = () => {
    if (
      !(isMultiTraitMode && Array.isArray(attributes)) ||
      attributes.length === 0
    ) {
      return attribute?.floorPrice?.token.unit
    }
    if (attributes.length === 1) {
      return attributes[0]?.floorPrice?.token.unit
    }
    const floorPrices = attributes
      .map(attr => attr?.floorPrice?.token.unit)
      .filter((price): price is number => typeof price === "number")
    return floorPrices.length > 0 ? Math.max(...floorPrices) : undefined
  }

  const getAggregateTraitTopOfferPrice = () => {
    if (
      !(isMultiTraitMode && Array.isArray(attributes)) ||
      attributes.length === 0
    ) {
      return attribute?.topOfferPrice?.token.unit
    }
    if (attributes.length === 1) {
      return attributes[0]?.topOfferPrice?.token.unit
    }
    const topOfferPrices = attributes
      .map(attr => attr?.topOfferPrice?.token.unit)
      .filter((price): price is number => typeof price === "number")
    return topOfferPrices.length > 0 ? Math.max(...topOfferPrices) : undefined
  }

  useSyncFormFields({
    fields: [
      {
        name: "offers.0.floorPrice",
        value: collection.floorPrice?.pricePerItem.token.unit,
      },
      {
        name: "offers.0.bestOffer",
        value: collection.topOffer?.pricePerItem.token.unit,
      },
      {
        name: "offers.0.traitFloorPrice",
        value: getAggregateTraitFloorPrice(),
      },
      {
        name: "offers.0.traitTopOfferPrice",
        value: getAggregateTraitTopOfferPrice(),
      },
    ],
    setValue: methods.setValue,
  })

  const [view, setView] = useState<"create" | "review" | "success" | "error">(
    "create",
  )
  const [isPriceWarningModalOpen, setIsPriceWarningModalOpen] = useState(false)
  const shouldConfirmPrice = offers[0]?.shouldConfirmPrice

  const handleFormSubmit = () => {
    if (shouldConfirmPrice) {
      setIsPriceWarningModalOpen(true)
    } else {
      setView("review")
    }
  }

  const showToast = useCallback(() => {
    // Show toast when action completes
    const { receiptStatus } = FlowState

    addCustomToast(
      () => (
        <CreateCollectionOfferToast
          imageUrl={collection.imageUrl || ""}
          price={{
            unit: methods.getValues("offers.0.price"),
            symbol: methods.getValues("offers.0.offerSymbol"),
          }}
          status={receiptStatus}
          trait={trait}
          traits={traits}
        />
      ),
      {
        id: "create-collection-offer-toast",
        duration: 15_000,
      },
    )
  }, [collection, methods, addCustomToast, trait, traits])

  return (
    <FormProvider {...methods}>
      {view === "create" && (
        <CreateCollectionOfferForm
          collection={collection}
          onSubmit={handleFormSubmit}
        />
      )}
      {view === "review" && (
        <CreateCollectionOfferFlowReview
          collection={collection}
          onBack={() => setView("create")}
          onError={() => {
            FlowActions.setReceiptStatus("FAILED")
            showToast()
            FlowActions.endFlow()
            setView("error")
          }}
          onSuccess={() => {
            FlowActions.setReceiptStatus("SUCCESS")
            FlowActions.onSuccess()
            showToast()
            FlowActions.endFlow()
          }}
        />
      )}
      <CollectionOfferPriceWarningModal
        offerSymbol={offerSymbol}
        onNext={() => setView("review")}
        open={isPriceWarningModalOpen}
        setIsOpen={setIsPriceWarningModalOpen}
      />
    </FormProvider>
  )
}

const useDefaultFormValues = (
  collection: ResultOf<typeof CreateCollectionOfferFlowBaseFragment>,
  trait?: Trait,
  traits?: Trait[],
  initialPrice?: Price,
): CreateCollectionOfferFormValues => {
  const [localStorageOfferExpirationMinutes] = useLocalStorage(
    OFFER_EXPIRATION_LOCAL_STORAGE,
    DEFAULT_EXPIRATION,
  )
  const [localStorageOfferExpirationCustom] = useLocalStorage<
    string | undefined
  >(OFFER_EXPIRATION_CUSTOM_EXPIRATION_LOCAL_STORAGE, undefined)

  return {
    expiration: {
      expirationType: localStorageOfferExpirationMinutes,
      customExpiration: localStorageOfferExpirationCustom
        ? new Date(localStorageOfferExpirationCustom)
        : undefined,
    },
    offers: [
      {
        id: collection.id,
        quantity: 1,
        applyCreatorFee: Boolean(collection.fees.totalCreatorFee.isRequired),
        isCreatorFeeRequired: Boolean(
          collection.fees.totalCreatorFee.isRequired,
        ),
        creatorFeeBasisPoints:
          collection.fees.totalCreatorFee.feeBasisPoints || 0,
        openSeaFeeBasisPoints: collection.fees.openseaFee?.feeBasisPoints || 0,
        price: initialPrice?.unit || Number.NaN,
        floorPrice: collection.floorPrice?.pricePerItem.token.unit,
        bestOffer: collection.topOffer?.pricePerItem.token.unit,
        maxQuantity: collection.stats.totalSupply,
        trait,
        traits,
        offerSymbol: collection.chain.offerCurrency.symbol,
        nativeCurrencySymbol: collection.chain.nativeCurrency.symbol,
        isWrappedNativeCurrency:
          collection.chain.offerCurrency.address ===
          collection.chain.wrappedNativeCurrency?.address,
        traitFloorPrice: undefined,
        traitTopOfferPrice: undefined,
      },
    ],
  }
}
